import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PublicHeader from "@/components/layout/PublicHeader";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { QRBlock } from "@/components/QRCode";
import { EventPeople } from "@/components/events/EventPeople";
import { SectionLoader } from "@/components/common";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDateLong, formatTime } from "@/lib/format";
import { mapEmbedSrc, directionsUrl, fullAddress } from "@/lib/fridays";
import { CalendarDays, MapPin, Clock, ArrowLeft, Users, CheckCircle2, Hourglass, Loader2, QrCode, Navigation, Lock, Mic, Image as ImageIcon } from "lucide-react";

function LocationMapCard({ loc }) {
  const [mapLoaded, setMapLoaded] = useState(false);
  if (!loc || !loc.address) return null;
  return (
    <Card className="mt-6 border-border bg-card" data-testid="event-detail-map-card">
      <CardContent className="p-6">
        <h2 className="font-serif text-xl font-semibold text-foreground">Getting there</h2>
        <div className="mt-2 flex items-start gap-2 text-sm text-muted-foreground">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <span><span className="font-medium text-foreground">{loc.name}</span>{loc.room ? ` · ${loc.room}` : ""}<br />{fullAddress(loc)}</span>
        </div>

        {Array.isArray(loc.directions) && loc.directions.length > 0 && (
          <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
            {loc.directions.map((d, i) => (
              <li key={i} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" /><span>{d}</span></li>
            ))}
          </ul>
        )}
        {loc.parking && (
          <p className="mt-3 text-sm text-muted-foreground"><span className="font-medium text-foreground">Parking:</span> {loc.parking}</p>
        )}

        <div className="relative mt-5 h-60 overflow-hidden rounded-xl border border-border bg-secondary sm:h-72">
          {!mapLoaded && <Skeleton className="absolute inset-0 h-full w-full" />}
          <iframe
            title={`Map of ${loc.name}`}
            src={mapEmbedSrc(loc)}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
            onLoad={() => setMapLoaded(true)}
            className={`h-full w-full border-0 transition-opacity duration-300 ${mapLoaded ? "opacity-100" : "opacity-0"}`}
            data-testid="event-detail-map-iframe"
          />
        </div>

        <Button asChild className="mt-4 bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="event-detail-get-directions-button">
          <a href={directionsUrl(loc)} target="_blank" rel="noopener noreferrer"><Navigation className="mr-1.5 h-4 w-4" />Get directions</a>
        </Button>
      </CardContent>
    </Card>
  );
}

export default function EventDetail() {
  const { eventId } = useParams();
  const { user, isPodcastEligible } = useAuth();
  const navigate = useNavigate();
  const [ev, setEv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [liveSession, setLiveSession] = useState(null);
  const [accessStatus, setAccessStatus] = useState(null);
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    if (user && !isPodcastEligible) {
      api.get("/podcast/my-access").then((r) => setAccessStatus(r.data)).catch(() => {});
    }
  }, [user, isPodcastEligible]);

  const requestAccess = async () => {
    setRequesting(true);
    try {
      const r = await api.post("/podcast/request-access");
      toast.success(r.data?.message || "Request sent");
      setAccessStatus((s) => ({ ...(s || {}), request_status: "pending" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send request");
    } finally { setRequesting(false); }
  };

  useEffect(() => {
    api.get("/hub/live").then((r) => {
      const d = r.data;
      if (d?.live && d.session?.event_id === eventId) setLiveSession(d.session);
      else setLiveSession(null);
    }).catch(() => {});
  }, [eventId]);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/events/${eventId}`);
      setEv(res.data);
    } catch (e) {
      toast.error("Event not found");
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  const doRsvp = async () => {
    if (!user) { navigate("/login"); return; }
    setActing(true);
    try {
      const res = await api.post(`/events/${eventId}/rsvp`);
      toast.success(res.data.message);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not RSVP");
    } finally { setActing(false); }
  };

  const cancelRsvp = async () => {
    setActing(true);
    try {
      const res = await api.delete(`/events/${eventId}/rsvp`);
      toast.success(res.data.message);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    } finally { setActing(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      {loading ? (
        <SectionLoader label="Loading event…" />
      ) : !ev ? (
        <div className="mx-auto max-w-3xl px-4 py-20 text-center">
          <p className="text-muted-foreground">This event could not be found.</p>
          <Button asChild className="mt-4"><Link to="/">Back home</Link></Button>
        </div>
      ) : (
        <>
          <div className="fc-hero-bg">
            <div className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6">
              <Link to="/#events" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> All events</Link>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Badge variant="secondary"><CalendarDays className="mr-1.5 h-3.5 w-3.5" />{formatDateLong(ev.date)}</Badge>
                {ev.is_featured && <Badge className="border border-[rgba(201,119,46,0.45)] bg-[rgba(201,119,46,0.22)] text-[#8A4A18]">Featured</Badge>}
              </div>
              <h1 className="mt-4 font-serif text-3xl font-semibold text-foreground sm:text-4xl">{ev.title}</h1>
              {ev.theme_title && <p className="mt-2 text-lg font-medium text-primary">Theme: {ev.theme_title}</p>}
            </div>
          </div>

          <div className="mx-auto grid w-full max-w-5xl gap-8 px-4 py-10 sm:px-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              {ev.flyer_image && (
                <Dialog>
                  <DialogTrigger asChild>
                    <button
                      type="button"
                      className="group relative mb-6 block w-full overflow-hidden rounded-2xl border border-border bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      data-testid="event-detail-flyer"
                    >
                      <img src={ev.flyer_image} alt={`${ev.title} flyer`} className="mx-auto max-h-[72vh] w-auto max-w-full object-contain transition-transform duration-300 group-hover:scale-[1.01]" />
                      <span className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-end gap-1 bg-gradient-to-t from-black/45 to-transparent p-3 text-xs font-medium text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                        <ImageIcon className="h-3.5 w-3.5" /> View full size
                      </span>
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-w-3xl border-border bg-card p-2" data-testid="event-detail-flyer-dialog">
                    <DialogHeader className="sr-only"><DialogTitle>{ev.title} flyer</DialogTitle></DialogHeader>
                    <img src={ev.flyer_image} alt={`${ev.title} flyer`} className="mx-auto max-h-[85vh] w-auto max-w-full rounded-lg object-contain" />
                  </DialogContent>
                </Dialog>
              )}
              {ev.cover_image && (
                <div className="mb-6 overflow-hidden rounded-2xl border border-border">
                  <AspectRatio ratio={16 / 9}><img src={ev.cover_image} alt={ev.title} className="h-full w-full object-cover" /></AspectRatio>
                </div>
              )}
              {ev.theme_description && (
                <Card className="mb-6 border-border bg-card"><CardContent className="p-6"><h2 className="font-serif text-xl font-semibold text-foreground">This week's theme</h2><p className="mt-2 leading-7 text-muted-foreground">{ev.theme_description}</p></CardContent></Card>
              )}
              {ev.description && (
                <Card className="border-border bg-card"><CardContent className="p-6"><h2 className="font-serif text-xl font-semibold text-foreground">About this event</h2><p className="mt-2 whitespace-pre-line leading-7 text-muted-foreground">{ev.description}</p></CardContent></Card>
              )}
              {ev.people?.length > 0 && (
                <div className="mt-6">
                  <EventPeople people={ev.people} />
                </div>
              )}
              <LocationMapCard loc={ev.location} />
              {ev.media?.length > 0 && (
                <div className="mt-6">
                  <h2 className="font-serif text-xl font-semibold text-foreground">Highlights</h2>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    {ev.media.map((m) => (
                      <Card key={m.media_id} className="overflow-hidden border-border">
                        <AspectRatio ratio={16 / 9} className="bg-secondary">
                          {m.type === "video" && m.url ? (
                            <iframe title={m.title} src={m.url} className="h-full w-full" allowFullScreen />
                          ) : (
                            <img src={m.data || m.url} alt={m.title || ""} className="h-full w-full object-cover" />
                          )}
                        </AspectRatio>
                        {m.title && <div className="p-3 text-sm font-medium text-foreground">{m.title}</div>}
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="lg:col-span-1">
              <Card className="sticky top-24 border-border bg-card shadow-[0_12px_30px_rgba(20,20,20,0.06)]" data-testid="event-detail-rsvp-card">
                <CardContent className="p-6">
                  {liveSession && (
                    <Button asChild className="mb-4 w-full bg-red-600 text-white hover:bg-red-700" data-testid="event-detail-join-hub">
                      <Link to={`/portal/hub/${liveSession.session_id}`}>
                        <span className="relative mr-2 flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white" /></span>
                        Join the live Podcast Hub
                      </Link>
                    </Button>
                  )}
                  <h3 className="font-serif text-lg font-semibold text-foreground">Event details</h3>
                  <div className="mt-4 space-y-3 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-primary" />{formatDateLong(ev.date)}</div>
                    {(ev.start_time || ev.end_time) && <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-primary" />{formatTime(ev.start_time)}{ev.end_time ? ` – ${formatTime(ev.end_time)}` : ""}</div>}
                    {ev.location && (
                      <div className="flex items-start gap-2"><MapPin className="mt-0.5 h-4 w-4 text-primary" /><span>{ev.location.name}{ev.location.address ? <><br />{ev.location.address}</> : null}{ev.location.city ? <><br />{ev.location.city}{ev.location.state ? `, ${ev.location.state}` : ""}</> : null}</span></div>
                    )}
                  </div>

                  <div className="my-5 h-px bg-border" />

                  <div className="mt-4">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="ghost" className="mt-2 w-full text-muted-foreground hover:text-foreground" data-testid="event-detail-share-button"><QrCode className="mr-2 h-4 w-4" />Share via QR</Button>
                      </DialogTrigger>
                      <DialogContent data-testid="event-detail-share-dialog">
                        <DialogHeader>
                          <DialogTitle className="font-serif">Share this event</DialogTitle>
                        </DialogHeader>
                        <p className="-mt-1 text-sm text-muted-foreground">Anyone who scans this code lands on this event page.</p>
                        <div className="flex justify-center py-2">
                          <QRBlock value={`${window.location.origin}/events/${eventId}`} size={200} fileName={`event-${eventId}`} printTitle={ev.title} printSubtitle={ev.date ? formatDateLong(ev.date) : "Friday Conversations"} testId="event-share-qr" />
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </>
      )}
      <Footer />
    </div>
  );
}
